import {uiBus} from "@/services/services";
import {DISPLAY_MODE, INSTALL_BUTTON_RESULTS} from "@/constants/system";

export interface IUpdateValueParameters {
  canInstall: boolean;
  isInstalled: boolean;
  displayMode: DISPLAY_MODE;
}


export class PWAInstallerService {
  public canInstall: boolean = false;
  public isInstalled: boolean = false;
  public deferredPrompt: any = null;
  public displayMode: DISPLAY_MODE = DISPLAY_MODE.BROWSER_TAB;

  private readonly listenerPromise: Promise<any>;

  constructor() {
    this.listenerPromise = new Promise<any>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
    this.addListeners();
  }

  public setUpdateValuesCallback(updateCallback: (params: IUpdateValueParameters) => any) {
    this.updateCallback = updateCallback;
    this.listenerPromise.then(() => this.updateValues());
  }

  public updateValues() {
    this.updateCallback({
      canInstall: this.canInstall,
      isInstalled: this.isInstalled,
      displayMode: this.displayMode,
    });
  }

  public addListeners(): any {
    Promise.all([
      this.beforeInstallPrompOperations(),
      this.appIsInstalledOperations(),
      this.domContentLoadedOperations(),
    ]).then(this.resolve());
  }

  public isRunningInStandaloneMode(): boolean {
    return this.displayMode !== DISPLAY_MODE.BROWSER_TAB;
  }

  public install() {
    let result: INSTALL_BUTTON_RESULTS = INSTALL_BUTTON_RESULTS.CAN_NOT_INSTALL;

    if (this.deferredPrompt !== null) {
      this.deferredPrompt.prompt();
      result = INSTALL_BUTTON_RESULTS.INSTALLING;
    }

    if (this.isInstalled) {
      result = INSTALL_BUTTON_RESULTS.IS_INSTALLED_ALREADY;
    }

    return result;
  }

  public showInstallButton(): Promise<any> {
    return this.listenerPromise.then(() => this.canInstall && !this.isRunningInStandaloneMode());
  }

  public beforeInstallPrompOperations() {
    return new Promise((resolve, myReject) => {

      window.addEventListener('beforeinstallprompt', (e) => {
        // Prevent the mini-infobar from appearing on mobile
        e.preventDefault();
        // Stash the event
        this.deferredPrompt = e;
        this.canInstall = true;
        resolve(this.canInstall);
        this.updateValues();
      });

      //  we need to return the promise, but also we have to make sure that, if we are waiting too much we resolve
      //  anyway (this event won't be triggered in all the domains or if we are already installed, or in the app);
      setTimeout(() => {
          resolve(this.canInstall);
        },
        1000,
      );

    });
  }


  public appIsInstalledOperations() {
    // When pwa is installed
    window.addEventListener('appinstalled', () => {
      this.canInstall = false;
      this.isInstalled = true;
      this.updateValues();
    });



    return Promise.resolve(); // this won't matter in the end because false is the default
  }

  public domContentLoadedOperations() {
    return new Promise((resolve, myReject) => {

      // Detect displayMode (if it is running in standalone or browser)
      window.addEventListener('DOMContentLoaded', () => {
        const myNavigator: any = window.navigator;
        // Detect in standalone mode in ios
        if (myNavigator.standalone) {
          this.displayMode = DISPLAY_MODE.STANDALONE_IOS;
        }
        if (window.matchMedia('(display-mode: standalone)').matches) {
          this.displayMode = DISPLAY_MODE.STANDALONE;
        }

        this.updateValues();
        resolve(this.displayMode);
      });
    });
  }

  private resolve: () => any = () => null;
  private reject: () => any = () => null;
  private updateCallback: (params: IUpdateValueParameters) => any = (param: IUpdateValueParameters): null => null;

}
